import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { TeacherDetail } from "@/components/teacher/teacher-detail";
import { pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(pages)/teachers/[teacherId]/page.tsx.
 * Four parallel queries:
 *   - teacher (with login expand)
 *   - batches (id-in filter built from teacher.batches array)
 *   - student count (filtered by teacher's batches)
 *   - exam count (filtered by teacher's batches + subjects)
 */
interface BatchRecord {
  id: string;
  name: string;
  exam_year?: string;
}

interface TeacherRecord {
  id: string;
  name: string;
  subjects: string[];
  qualifications?: string;
  bio?: string;
  batches: string[];
  created?: string;
  login?: string;
  // Phase 4: denormalized from auth, read directly off the record.
  email?: string;
  phone?: string;
}

function teacherQuery(teacherId: string, authId: string | undefined) {
  return {
    queryKey: ["teacher", teacherId, authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("teacher").getOne<TeacherRecord>(teacherId, {
        fields: "id,name,subjects,qualifications,bio,batches,created,email,phone,login",
      }),
  };
}

function batchesQuery(teacherId: string, batchIds: string[], authId: string | undefined) {
  return {
    queryKey: ["teacher", teacherId, "batches", batchIds.join(","), authId ?? "anon"] as const,
    queryFn: async () => {
      if (batchIds.length === 0) return { items: [] as BatchRecord[] };
      const filter = batchIds.map((id) => `id = '${id}'`).join(" || ");
      const res = await pb.collection("batch").getList<BatchRecord>(1, batchIds.length, {
        filter,
        skipTotal: true,
        fields: "id,name,exam_year",
      });
      return { items: res.items };
    },
  };
}

function studentCountQuery(teacherId: string, batchIds: string[], authId: string | undefined) {
  return {
    queryKey: [
      "teacher",
      teacherId,
      "student-count",
      batchIds.join(","),
      authId ?? "anon",
    ] as const,
    queryFn: async () => {
      if (batchIds.length === 0) return { totalItems: 0 };
      const filter = batchIds.map((id) => `batch = '${id}'`).join(" || ");
      // Only totalItems is consumed.
      return pb.collection("student").getList(1, 1, { filter, fields: "id" });
    },
  };
}

function examCountQuery(
  teacherId: string,
  batchIds: string[],
  subjects: string[],
  authId: string | undefined,
) {
  return {
    queryKey: [
      "teacher",
      teacherId,
      "exam-count",
      batchIds.join(","),
      subjects.join(","),
      authId ?? "anon",
    ] as const,
    queryFn: async () => {
      if (batchIds.length === 0) return { totalItems: 0 };
      const batchPart = batchIds.map((id) => `batch = '${id}'`).join(" || ");
      // Botany/Zoology teachers own Biology exams — exam.type has no BOT/ZOO
      // since the 2026-07 data migration folded both into BIO.
      const examTypes = [
        ...new Set(["MODEL", ...subjects.map((s) => (s === "BOT" || s === "ZOO" ? "BIO" : s))]),
      ];
      const subjectPart = examTypes.map((s) => `type = '${s}'`).join(" || ");
      const filter = `(${batchPart}) && (${subjectPart})`;
      // Only totalItems is consumed.
      return pb.collection("exam").getList(1, 1, { filter, fields: "id" });
    },
  };
}

export const Route = createFileRoute("/_app/teachers/$teacherId")({
  loader: async ({ context, params }) => {
    const authId = pb.authStore.model?.id;
    const teacher = await context.queryClient.ensureQueryData(
      teacherQuery(params.teacherId, authId),
    );
    const batchIds = teacher.batches ?? [];
    await Promise.all([
      context.queryClient.ensureQueryData(batchesQuery(params.teacherId, batchIds, authId)),
      context.queryClient.ensureQueryData(studentCountQuery(params.teacherId, batchIds, authId)),
      context.queryClient.ensureQueryData(
        examCountQuery(params.teacherId, batchIds, teacher.subjects ?? [], authId),
      ),
    ]);
  },
  component: TeacherDetailPage,
});

function TeacherDetailPage() {
  const { teacherId } = Route.useParams();
  const authId = pb.authStore.model?.id;

  const { data: teacher } = useSuspenseQuery(teacherQuery(teacherId, authId));
  const batchIds = teacher.batches ?? [];
  const { data: batchesData } = useSuspenseQuery(batchesQuery(teacherId, batchIds, authId));
  const { data: studentCount } = useSuspenseQuery(studentCountQuery(teacherId, batchIds, authId));
  const { data: examCount } = useSuspenseQuery(
    examCountQuery(teacherId, batchIds, teacher.subjects ?? [], authId),
  );

  useDocumentTitle(teacher.name);

  return (
    <TeacherDetail
      teacher={teacher}
      batches={batchesData.items}
      studentCount={studentCount.totalItems}
      examCount={examCount.totalItems}
    />
  );
}
